import { Component, Inject, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UserSearchResult } from 'services/application/application.service';
import { ConfigService } from 'services/config/config.service';
import { ErrorMessageService } from 'services/errormsg/errormsg.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService } from 'services/user/user.service';

import { BaseDialogOptions } from '../dialogoptions';

@Component({
    selector: 'dialog-usersearch-component',
    styleUrls: ['usersearch.dialogcomponent.scss'],
    templateUrl: 'usersearch.dialogcomponent.html',
})
export class UserSearchDialogComponent implements OnInit, OnDestroy {
    @ViewChild('lastname') LastnameInput: ElementRef;
    @ViewChild('scrollpanel') ScrollPanel: ElementRef;

    private _subrelease: Subject<void> = new Subject<void>();

    title: string;
    message: string;
    oktext: string;
    canceltext: string;

    AllDistrictHeaders: string[] = ['LastName', 'FirstName', 'SAPID', 'PositionNo', 'LocalJobTitle', 'Email', 'Actions'];
    AllJudgeHeaders: string[] = ['LastName', 'FirstName', 'SAPID', 'Username', 'LocalJobTitle', 'Email', 'Actions'];
    AwardID: number;
    ConfirmationIsUp: boolean;
    ForcedLocationNumber: string;
    JudgeSearch: boolean;
    NoData: boolean;
    ResultsData: MatTableDataSource<UserSearchResult>;
    SearchForm: FormGroup;
    SmallDisplay: boolean;
    UserSearchConducted: boolean;
    SearchResults: UserSearchResult[];
    VisibleColumns: string[];
    VisibleHeaders: string[];
    showIcon: boolean;
    iconcolor: string;
	iconname: string;
	SchoolYear: string;

    constructor(
        private dialogRef: MatDialogRef<UserSearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: BaseUserSearchDialogOptions,
        private configSvc: ConfigService,
        private errorSvc: ErrorMessageService,
        private fb: FormBuilder,
        private http: HttpClient,
        private screenSizeSvc: ScreenSizeService,
        private splashSvc: SplashService,
        private userSvc: UserService
    ) {
        this.title = data.TitleText || 'Information';
        this.message = data.MessageText || '';
        this.oktext = data.OKText || 'OK';
        this.canceltext = data.CancelText || 'Cancel';
        this.AwardID = data.AwardID;
        this.ForcedLocationNumber = data.ForcedLocationNumber || null;
		this.JudgeSearch = data.JudgeSearch || false;
		this.SchoolYear = data.SchoolYear;

        this.showIcon = data.ShowIcon || false;
        this.iconcolor = data.IconColor || 'primary';
        this.iconname = data.IconName || '';
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.NoData = true;
        self.SearchResults = [];
        self.ResultsData = new MatTableDataSource<UserSearchResult>([<UserSearchResult>{}]);
        self.SearchForm = self.fb.group({
            FirstName: [null],
            LastName: [null],
            SAPID: [null, Validators.pattern(/^\d+$/)],
            Username: [null],
        });
        self.UserSearchConducted = false;

        // Watch for screen size updates
        self.screenSizeSvc.ScreenSizeSub.pipe(takeUntil(self._subrelease)).subscribe(size => {
            self.SmallDisplay = size.IsSmallSize;
            self.VisibleHeaders = self.SmallDisplay
                ? ['SmallDisplay']
                : self.JudgeSearch
                ? self.AllJudgeHeaders.slice()
                : self.AllDistrictHeaders.slice();
            self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
        });

        // First time size initialization
        self.SmallDisplay = self.screenSizeSvc.ScreenSize.IsSmallSize;
    }

    CancelHandler() {
        this.dialogRef.close(null);
    }

    OKHandler() {
        this.dialogRef.close(null);
    }

    HandleUserSearch() {
        const self = this;

        const form = self.SearchForm.value;

        // If this is a district user search, there must be at least 1 of either first name, last name, or SAPID provided.
        // We will not allow a search on 300000+ users without something!
        if (!self.JudgeSearch && (!form.FirstName && !form.LastName && !form.SAPID)) {
            self.splashSvc.SnackbarAlert('You must provide at least one search option in order to conduct a search');
            return;
        }

        // If this is a full judge user search, there's 4 criteria to use instead
        if (this.JudgeSearch && (!form.FirstName && !form.LastName && !form.SAPID && !form.Username)) {
            self.splashSvc.SnackbarAlert('You must provide at least one search option in order to conduct a search');
            return;
        }

        // Pad SAPID up to 8 characters, if it is provided.
        if (form.SAPID && form.SAPID.length < 8) {
            form.SAPID = ('00000000' + form.SAPID).slice(-8);
        }

        // Perform the search, different routine for judge vs. district user search.
        (self.JudgeSearch
            ? // Judge search
              self.http.post<UserSearchResult[]>(this.configSvc.hostAddress + `Api/Services/App/SearchForJudgeUser`, {
                  AuthToken: self.userSvc.User.AuthToken || '',
                  CurrentRole: self.userSvc.User.CurrentRole || '',
                  AwardID: self.AwardID || '',
                  FirstName: form.FirstName || '',
                  LastName: form.LastName || '',
                  SAPID: form.SAPID || '',
                  Username: form.Username || '',
              })
            : // District user search
              self.http.post<UserSearchResult[]>(this.configSvc.hostAddress + `Api/Services/App/SearchForUser`, {
                  AuthToken: self.userSvc.User.AuthToken || '',
                  CurrentRole: self.userSvc.User.CurrentRole || '',
				  AwardID: self.AwardID || '',
				  SchoolYear: self.SchoolYear,
                  FirstName: form.FirstName || '',
                  LastName: form.LastName || '',
                  SAPID: form.SAPID || '',
                  SchoolNumber: self.ForcedLocationNumber || ''
              })
        ).pipe(takeUntil(self._subrelease)).subscribe(
            users => {
                // Update the search results
                self.SearchResults = users;
                self.NoData = !users || !users.length;
                self.ResultsData.data = self.NoData ? [<UserSearchResult>{}] : users;
                self.UserSearchConducted = true;

                self.VisibleHeaders = self.SmallDisplay
                    ? ['SmallDisplay']
                    : self.JudgeSearch
                    ? self.AllJudgeHeaders.slice()
                    : self.AllDistrictHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                // If on a small display, scroll the scroll panel all the way downward
                if (self.SmallDisplay) {
                    window.setTimeout(function() {
                        const maxScroll = (<HTMLDivElement>self.ScrollPanel.nativeElement).scrollHeight;

                        (<HTMLDivElement>self.ScrollPanel.nativeElement).scrollTop = maxScroll;
                    }, 0);
                }
            },
            error => {
                const msg = self.errorSvc.RetrieveServiceErrorMessage(error);

                self.splashSvc.SnackbarError(msg, 5000);
            }
        );
    }

    ResetForm() {
        this.SearchForm.reset();
        if (this.LastnameInput) {
            (<HTMLInputElement>this.LastnameInput.nativeElement).focus();
        }
    }

    SelectUser(user: UserSearchResult) {
        if (!user) {
            return;
        }

        this.dialogRef.close(user);
    }
}

export class BaseUserSearchDialogOptions extends BaseDialogOptions {
    AwardID?: number;
    ForcedLocationNumber?: string;
	JudgeSearch?: boolean;
	SchoolYear: string;
}
